import {useQueryClient} from '@tanstack/react-query';
import React, {ReactNode, createContext, useContext, useEffect, useState} from 'react';

import {distributorT} from '@/lib/types/distributor.types';
import {marketT} from '@/lib/types/market.types';
import {marketDistributorWithDistributorT} from '@/lib/types/marketDistributor.types';
import {getValidOrderProductsReturnT} from '@/lib/types/rpcFunctionReturns.types';

import {SupabaseInstance} from '@/lib/utils';

import {useDataQuery} from '@/hooks';

import {useNotifications} from '@/providers/components/notificationsProvider';

type orderContextT = {
  error: string;
  isSubmitting: boolean;
  acknowledged: boolean;
  setAcknowledged: (acknowledged: boolean) => void;
  setAcknowledging: (acknowledged: boolean) => void;
  acknowledging: boolean;
  market: marketT | null;
  setMarket: (market: marketT | null) => void;
  distributor: distributorT | null;
  setDistributor: (distributor: distributorT | null) => void;
  orderItems: {[key: number]: number};
  updateOrderItem: (productId: number, quantity: number) => void;
  resetOrderItems: () => void;
  handleSubmitOrder: () => void;
  showMarketSearch: boolean;
  setShowMarketSearch: (show: boolean) => void;
  showDistributorSearch: boolean;
  setShowDistributorSearch: (show: boolean) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  validOrderProducts: getValidOrderProductsReturnT['data'] | null;
  markets: marketT[];
  distributors: marketDistributorWithDistributorT[];
  totals: getValidOrderProductsReturnT['counts'] | null;
};

const OrderDataContext = createContext<orderContextT | undefined>(undefined);

export const useCreateOrder = () => {
  const context = useContext(OrderDataContext);
  if (!context) {
    throw new Error('useCreateOrder must be used within a CreateOrderProvider');
  }
  return context;
};

interface OrderDataProviderProps {
  children: ReactNode;
}

export const CreateOrderProvider: React.FC<OrderDataProviderProps> = ({children}) => {
  // Hooks
  const queryClient = useQueryClient();
  const {toast} = useNotifications();

  // State
  const [market, setMarket] = useState<marketT | null>(null);
  const [distributor, setDistributor] = useState<distributorT | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [orderItems, setOrderItems] = useState<{[key: number]: number}>({});
  const [showMarketSearch, setShowMarketSearch] = useState(false);
  const [showDistributorSearch, setShowDistributorSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [validOrderProducts, setValidOrderProducts] = useState<getValidOrderProductsReturnT['data'] | null>(null);
  const [isDistributorChanged, setIsDistributorChanged] = useState(false);
  const [totals, setTotals] = useState<getValidOrderProductsReturnT['counts'] | null>(null);

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({queryKey: ['Order']});
    await queryClient.refetchQueries({queryKey: ['Order']});
  };

  // Market Hook
  const {data: {data: markets} = {data: []}} = useDataQuery({
    table: 'Market',
    limit: 10,
    searchTerm,
    searchColumn: 'market_name',
  });

  // Distributor Hook
  const {data: {data: distributors} = {data: []}} = useDataQuery({
    table: 'Market_Distributor',
    columns: ['*', ['Distributor', ['*']]],
    limit: 10,
    searchTerm,
    searchColumn: 'Distributor.distributor_name',
    filters: [
      {
        column: 'fk_market_id',
        operator: 'eq',
        value: market?.market_id,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    enabled: !!market,
  });

  const {
    data: {data: distributorBrands} = {data: []},
    isLoading: isDistributorBrandsLoading,
    refetch: refetchDistributorBrands,
  } = useDataQuery({
    table: 'Distributor_Brand',
    columns: ['*'],
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'eq',
        value: distributor?.distributor_id,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    enabled: !!distributor && !!market,
  });

  useEffect(() => {
    if (market) {
      setDistributor(null);
      setBrandIds([]);
      setValidOrderProducts(null);
      resetOrderItems();
    }
  }, [market]);

  useEffect(() => {
    if (distributor) {
      setIsDistributorChanged(true);
      setBrandIds([]);
      setValidOrderProducts(null);
      refetchDistributorBrands();
      resetOrderItems();
    }
  }, [distributor, refetchDistributorBrands]);

  useEffect(() => {
    if (isDistributorChanged && !isDistributorBrandsLoading) {
      const newBrandIds = distributorBrands.map(db => db.fk_brand_id);
      setBrandIds(newBrandIds);
      setIsDistributorChanged(false);
    }
  }, [isDistributorChanged, isDistributorBrandsLoading, distributorBrands]);

  useEffect(() => {
    const fetchValidOrderProducts = async () => {
      if (brandIds.length > 0 && market) {
        const {data, error} = await SupabaseInstance.supabase.rpc('get_valid_order_products', {
          brand_ids: brandIds,
          market_id: market?.market_id,
        });

        if (error) {
          console.error('Error fetching valid order products:', error);
        } else {
          if (data && typeof data === 'object' && 'data' in data && 'counts' in data) {
            setValidOrderProducts(data.data as getValidOrderProductsReturnT['data']);
            setTotals(data.counts as getValidOrderProductsReturnT['counts']);
          }
        }
      }
    };

    fetchValidOrderProducts();
  }, [brandIds, market]);

  const updateOrderItem = (productId: number, quantity: number) => {
    setOrderItems(prev => {
      const newItems = {...prev};
      if (quantity === 0) {
        delete newItems[productId];
      } else {
        newItems[productId] = quantity;
      }
      return newItems;
    });
  };

  const resetOrderItems = () => {
    setOrderItems({});
  };

  const handleSubmitOrder = async () => {
    if (!market || !distributor) {
      toast.error('Please select a market and distributor before submitting the order.');
      return;
    }
    const {data: user, error: userError} = await SupabaseInstance.supabase.auth.getUser();

    if (userError) {
      console.error('Error fetching user:', userError);
      toast.error('Failed to fetch user. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const {data, error} = await SupabaseInstance.supabase.rpc('create_distributor_order', {
        p_order_items: orderItems,
        p_market_id: market.market_id,
        p_distributor_id: distributor.distributor_id,
        user_uid: user.user.id,
      });

      if (error) throw error;

      if (data) {
        toast.success('Order created successfully!');
        // Reset the form or navigate to a new page
        resetOrderItems();
        setMarket(null);
        setDistributor(null);
        invalidateQuery();
      } else {
        toast.error('Failed to create order. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Error submitting order in provider:', error);
      setError('An error occurred while submitting the order. Please try again.');
      toast.error(`Failed to submit order: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const value = {
    error,
    isSubmitting,
    acknowledging,
    setAcknowledging,
    acknowledged,
    setAcknowledged,
    market,
    setMarket,
    distributor,
    setDistributor,
    orderItems,
    updateOrderItem,
    resetOrderItems,
    handleSubmitOrder,
    showMarketSearch,
    setShowMarketSearch,
    showDistributorSearch,
    setShowDistributorSearch,
    searchTerm,
    setSearchTerm,
    validOrderProducts,
    markets,
    distributors,
    totals,
  };

  return <OrderDataContext.Provider value={value}>{children}</OrderDataContext.Provider>;
};
